// @flow
import React, { useState, useRef, useEffect } from 'react'
import Avatar from '@components/app/avatar'
import Editor from '@components/app/editor/comment'
import Button from '@components/common/button'
import Spinner from '@components/common/spinner'
import { deleteComment, updateComment } from '@data/api/comment'
import useMounted from '@/hooks/mounted'
import { formatDate, toISO } from '@utils/date'
import { selectUnit } from '@formatjs/intl-utils'
import dayjs from 'dayjs'
import Link from 'next/link'
import {
  FormattedMessage,
  FormattedRelativeTime,
  // IntlShape,
} from 'react-intl'
import CommentInput from './input'
import Reaction from './reaction'
import styles from './comments.module.scss'
import Comments from './index'
// import { nl2br } from '@utils/string'
// eslint-disable-next-line import/no-cycle

interface CommentType {
  id: string
  text: string
  // eslint-disable-next-line camelcase
  created_at: Date
  like?: number
  user: {
    name: string
    username: string
    // eslint-disable-next-line camelcase
    user_image: string
    // eslint-disable-next-line camelcase
    user_id: string
  }
  comments: number
  claps: number
  plus: number
  minus: number
  Laugh: number
  Hooray: number
  Confused: number
  Heart: number
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any
  owner: string
  comment: CommentType
  type: string
  reload: () => void
}

const Comment = ({ user, owner, comment, type, reload }: Props) => {
  const [ openReaction, setReaction ] = useState(false)
  // const [claps, setClaps] = useState(comment.claps || 0)
  const [ isSubmitting, setSubmitting ] = useState(false)
  const [ openReply, setOpenReply ] = useState(false)
  const [ openReplayInput, setOpenReplyInput ] = useState(false)
  const [ editting, setEditting ] = useState(false)
  const [ deleteConfirm, setDeleteConfirm ] = useState(false)
  const [ commentText, setCommentText ] = useState(comment.text || '')
  const [ commentAmount, setCommentAmount ] = useState(comment.comments || 0)
  const commentTextRef = useRef(comment.text)
  const replyRef = useRef(null)
  const mounted = useMounted()

  const { value: timeValue, unit: timeUnit } = selectUnit(
    dayjs(comment.created_at).toDate(),
  )

  const onEdit = async () => {
    setEditting(false)
    setSubmitting(true)
    await updateComment(comment.id, commentText)
    commentTextRef.current = commentText
    setSubmitting(false)
  }
  const onCancel = async () => {
    setEditting(false)
    setSubmitting(true)
    setCommentText(commentTextRef.current)
    setTimeout(() => {
      setSubmitting(false)
    }, 500)
  }

  const onDelete = async () => {
    setSubmitting(true)
    await deleteComment(comment.id)
    setDeleteConfirm(false)
    setSubmitting(false)
    reload()
  }

  // const onLikeClick = () => setClaps(claps + 1)

  const onSubmit = (text: string) => {
    replyRef.current.onSubmit(text)
    setCommentAmount(commentAmount + 1)
    setOpenReply(true)
    setOpenReplyInput(false)
  }

  useEffect(() => {
    setCommentAmount(comment.comments)
  }, [ comment.comments ])

  return (
    <div>
      <div className={`${styles.comment} d-flex mb-4`}>
        <div className="flex0 mr-3">
          <Link
            href={`/user/${comment.user.username}`}
          >
            <Avatar
              src={comment.user.user_image}
              alt={comment.user.name}
              className={styles.avatar}
            />
          </Link>
        </div>
        <div className="flex1">
          <div className={`${styles.commentMain} mb-1 pb-1`}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Link
                href={`/user/${comment.user.username}`}
                className="alt tx-14 tx-black tx-w600"
              >
                {comment.user.name}
              </Link>
              <time
                className="tx-12 tx-g500 mr-3"
                dateTime={toISO(comment.created_at)}
              >
                {mounted ? (
                  <FormattedRelativeTime
                    value={timeValue}
                    unit={timeUnit}
                    numeric="auto"
                  />
                ) : (
                  formatDate(comment.created_at, 'YYYY/MM/DD HH:mm')
                )}
              </time>
            </div>
            <div className="ps-relative">
              {!isSubmitting ? (
                <>
                  <Editor
                    className="mb-3"
                    comment={commentText}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any) => setCommentText(e)}
                    readOnly={!editting}
                  />
                  {editting && (
                    <div className="tx-right">
                      <Button className="link" onClick={onCancel}>
                        <FormattedMessage
                          id="app.cancel"
                          defaultMessage="Cancel"
                        />
                      </Button>
                      <Button className="primary" onClick={onEdit}>
                        <FormattedMessage id="app.save" defaultMessage="Save" />
                      </Button>
                    </div>
                  )}
                  {deleteConfirm && (
                    <>
                      <div className="tx-center tx-g900 tx-14 py-3">
                        <FormattedMessage
                          id="comment.deleteConfirm"
                          defaultMessage="Are you sure you want to delete this comment?"
                        />
                      </div>
                      <div className="tx-right">
                        <Button className="link" onClick={() => setDeleteConfirm(false)}>
                          <FormattedMessage
                            id="app.cancel"
                            defaultMessage="Cancel"
                          />
                        </Button>
                        <Button className="primary" onClick={onDelete}>
                          <FormattedMessage id="app.delete" defaultMessage="Delete" />
                        </Button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="tx-center">
                  <Spinner size="1rem" />
                  <span className="ml-3">Loading...</span>
                </div>
              )}
            </div>
            <div className={`d-flex ${styles.actions}`}>
              {/* いいね */}
              {/* <button
                type="button"
                className="btn-chromeless tx-black tx-12 mr-3"
                aria-label="Like"
                onClick={onLikeClick}
              >
                <i className="solicon-heart mr-2" />
                {
                  claps > 0 ? (
                    <span>{claps}</span>
                  ) : (
                    <FormattedMessage
                      id="app.like"
                      defaultMessage="Like"
                    />
                  )
                }
              </button> */}
              <div
                className={`d-flex align-items-center ${styles.reactions} ${
                  openReaction && styles.reactionsActive
                }`}
              >
                <Reaction
                  type="plus"
                  emoji="👍"
                  count={comment.plus}
                  commentId={comment.id}
                  disabled={!user.username}
                />
                <Reaction
                  type="minus"
                  emoji="👎"
                  count={comment.minus}
                  commentId={comment.id}
                  disabled={!user.username}
                />
                <Reaction
                  type="Laugh"
                  emoji="😄"
                  count={comment.Laugh}
                  commentId={comment.id}
                  disabled={!user.username}
                />
                <Reaction
                  type="Hooray"
                  emoji="🎉"
                  count={comment.Hooray}
                  commentId={comment.id}
                  disabled={!user.username}
                />
                <Reaction
                  type="Confused"
                  emoji="😕"
                  count={comment.Confused}
                  commentId={comment.id}
                  disabled={!user.username}
                />
                <Reaction
                  type="Heart"
                  emoji="❤️"
                  count={comment.Heart}
                  commentId={comment.id}
                  disabled={!user.username}
                />
              </div>
              <button
                type="button"
                className={`tx-black tx-12 ${styles.hoverBtn}`}
                aria-label="Reply"
                onClick={() => setReaction(!openReaction)}
              >
                <i className="solicon-face-smile" />
                <span className="d-none d-md-inline ml-1">
                  <FormattedMessage
                    id="app.reaction"
                    defaultMessage="リアクション"
                  />
                </span>
              </button>
              {type !== 'comment' && (
                <button
                  type="button"
                  className={`tx-black tx-12 ml-md-3 ml-1 ${styles.hoverBtn}`}
                  aria-label="Reply"
                  onClick={() => setOpenReplyInput(!openReplayInput)}
                >
                  <i className="solicon-comment-alt" />
                  <span className="d-none d-md-inline ml-1">
                    <FormattedMessage id="app.reply" defaultMessage="Reply" />
                  </span>
                </button>
              )}
              {comment.user.user_id === user.id && (
                <button
                  type="button"
                  className={`tx-black tx-12 ml-md-3 ml-1 ${styles.hoverBtn}`}
                  onClick={() => {
                    setEditting(!editting)
                    setDeleteConfirm(false)
                  }}
                >
                  <i className="solicon-pencil" />
                  <span className="d-none d-md-inline ml-1">
                    <FormattedMessage id="app.edit" defaultMessage="Edit" />
                  </span>
                </button>
              )}
              {(comment.user.user_id === user.id || owner === user.id) && (
                <button
                  type="button"
                  className={`tx-black tx-12 ml-md-3 ml-1 ${styles.hoverBtn}`}
                  onClick={() => {
                    setDeleteConfirm(!deleteConfirm)
                    setEditting(false)
                  }}
                >
                  <i className="solicon-trash" />
                  <span className="d-none d-md-inline ml-1">
                    <FormattedMessage id="app.delete" defaultMessage="Delete" />
                  </span>
                </button>
              )}
            </div>
          </div>
          {openReplayInput && (
            <div className="mt-3">
              {user.username ? (
                <CommentInput
                  onSubmit={onSubmit}
                  user={user}
                  submitting={isSubmitting}
                />
              ) : (
                <div className="tx-center tx-g900 tx-14 py-5">
                  <FormattedMessage
                    id="comment.not.signed"
                    defaultMessage="Please sign in to leave comments."
                  />
                </div>
              )}
            </div>
          )}
          {type !== 'comment' && commentAmount > 0 && (
            <button
              type="button"
              className="btn-chromeless tx-g500 tx-12 mr-3"
              aria-label="Reply"
              onClick={() => setOpenReply(!openReply)}
            >
              <i
                className={`icon solicon-angle-down mr-2 ${
                  openReply && styles.replyActiveIcon
                }`}
              />
              {/* <FormattedMessage
                  id="app.reply"
                  defaultMessage="Reply"
                /> */}
              <span>
                {!openReply
                  ? `${commentAmount}件の返信を表示`
                  : `${commentAmount}件の返信を非表示`}
              </span>
            </button>
          )}
          <div className={`mt-3 ${openReply ? 'd-block' : 'd-none'}`}>
            <Comments type="comment" id={comment.id} owner={owner} ref={replyRef} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Comment
